import { useUnit } from 'effector-react'
import { useMutation, useQueryClient } from 'react-query'

import { useToast } from '@/components/ui/toast.tsx'
import { $creatorId } from '@/models/creators.ts'
import { massMessageService } from '@/modules/online-mass-messages/service/mass-message-service.ts'
import { handleError } from '@/utils/error'

import type { BulkMessage, MassMessageCreateRequest } from '../types'

export function useMassMessageMutations() {
  const queryClient = useQueryClient()
  const creatorId = useUnit($creatorId)
  const toast = useToast()

  const handleMessageCreation = (values: MassMessageCreateRequest) => {
    return massMessageService.createMassMessage({
      creatorId: creatorId!,
      requestBody: values,
    })
  }

  const handleMessageUpdate = ({
    messageId,
    values,
  }: {
    messageId: number | undefined
    values: Partial<MassMessageCreateRequest> & {
      status?: BulkMessage['status']
    }
  }) => {
    return massMessageService.updateMassMessage({
      messageId,
      creatorId: creatorId!,
      requestBody: values,
    })
  }

  const updateMutation = useMutation(handleMessageUpdate, {
    onSettled: () =>
      queryClient.invalidateQueries(['mass-messages', creatorId]),
    onSuccess: () => {
      toast.success('Successfully updated message')
    },
    onError: (error) => {
      handleError(error, 'Failed to update message')
    },
  })

  const createMutation = useMutation(handleMessageCreation, {
    onSettled: () =>
      queryClient.invalidateQueries(['mass-messages', creatorId]),
    onSuccess: () => {
      toast.success('Successfully created message')
    },
    onError: (error) => {
      handleError(error, 'Failed to create message')
    },
  })

  return {
    updateMutation,
    createMutation,
  }
}
